import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Mail, Lock, User, ArrowLeft, Eye, EyeOff, CheckCircle, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';

const tb = { fontFamily: "'Tiro Bangla', serif" };
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Education level fallback image
const EDU_FALLBACK = {
    school: '/images/hero-student.png',
    college: '/images/hero-student.png',
    graduate: '/images/hero-student.png',
};

// ===== Eye Toggle Password Input =====
const PasswordInput = ({ id, placeholder, value, onChange, required, minLength, 'data-testid': dt }) => {
    const [show, setShow] = useState(false);
    return (
        <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <Input
                id={id}
                type={show ? 'text' : 'password'}
                placeholder={placeholder}
                value={value}
                onChange={onChange}
                className="pl-10 pr-10 bg-gray-50/80 border-gray-200/80 focus:border-purple-400 focus:ring-purple-200 rounded-xl h-11 text-sm"
                required={required}
                minLength={minLength}
                data-testid={dt}
            />
            <button
                type="button"
                onClick={() => setShow(s => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                tabIndex={-1}
            >
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
        </div>
    );
};

// ===== Forgot Password Modal =====
const ForgotPasswordModal = ({ onClose }) => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            await axios.post(`${API}/auth/forgot-password`, { email });
            setSent(true);
        } catch {
            setError('কিছু একটা ভুল হয়েছে। আবার চেষ্টা করুন।');
        }
        setLoading(false);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
            <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl p-6">
                <h2 className="text-lg font-bold text-gray-800 mb-1" style={tb}>পাসওয়ার্ড ভুলে গেছেন?</h2>
                <p className="text-xs text-gray-400 mb-4" style={tb}>আপনার রেজিস্টার্ড ইমেইলে reset link পাঠানো হবে</p>

                {!sent ? (
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {error && <p className="text-xs text-red-500 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
                        <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" />
                            <Input
                                type="email"
                                placeholder="your@email.com"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                className="pl-10 rounded-xl border-gray-200"
                                required
                                data-testid="forgot-email"
                            />
                        </div>
                        <div className="flex gap-2">
                            <Button type="button" variant="outline" className="flex-1 rounded-full text-sm" onClick={onClose}>বাতিল</Button>
                            <Button type="submit" disabled={loading}
                                className="flex-1 rounded-full text-sm bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:opacity-90">
                                {loading ? 'অপেক্ষা করুন...' : 'Reset Link পাঠান'}
                            </Button>
                        </div>
                    </form>
                ) : (
                    <div className="space-y-4">
                        <div className="flex items-center gap-2 text-green-600 bg-green-50 px-3 py-2 rounded-xl">
                            <CheckCircle className="w-4 h-4 shrink-0" />
                            <p className="text-xs font-medium" style={tb}>আপনার ইমেইলে password reset link পাঠানো হয়েছে!</p>
                        </div>
                        <p className="text-[11px] text-gray-500" style={tb}>
                            <strong>{email}</strong> ইমেইলে reset link পাঠানো হয়েছে। ইমেইল চেক করুন (spam folder-ও দেখুন)। Link ২ ঘণ্টার মধ্যে ব্যবহার করতে হবে।
                        </p>
                        <Button className="w-full rounded-full text-sm bg-gradient-to-r from-purple-600 to-pink-600 text-white" onClick={onClose}>
                            ঠিক আছে
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};

// ===== Main Auth Component =====
const Auth = () => {
    const navigate = useNavigate();
    const location = window.location;
    const { login, register, verifyOtp } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [showForgot, setShowForgot] = useState(false);
    const [activeTab, setActiveTab] = useState(() => new URLSearchParams(location.search).get('tab') === 'register' ? 'register' : 'login');

    const [loginEmail, setLoginEmail] = useState('');
    const [loginPassword, setLoginPassword] = useState('');

    const [registerName, setRegisterName] = useState('');
    const [registerEmail, setRegisterEmail] = useState('');
    const [registerPassword, setRegisterPassword] = useState('');
    const [registerMobile, setRegisterMobile] = useState('');
    const [emailWarning, setEmailWarning] = useState('');

    // Level images
    const [levelImages, setLevelImages] = useState({});
    const eduLevel = localStorage.getItem('bk_edu_level') || 'school';

    React.useEffect(() => {
        axios.get(`${API}/level-images`).then(r => setLevelImages(r.data || {})).catch(() => {});
    }, []);
    const heroImg = levelImages[eduLevel]?.image_url || EDU_FALLBACK[eduLevel] || '/images/hero-student.png';

    // Common email domain typo detection
    const checkEmailTypo = (email) => {
        if (!email || !email.includes('@')) { setEmailWarning(''); return; }
        const domain = email.split('@')[1]?.toLowerCase() || '';
        if (!domain) { setEmailWarning(''); return; }
        const typoMap = {
            'gmail.com': ['gmal.com','gmai.com','gmial.com','gemail.com','gamil.com','gmeil.com','gmaill.com','gnail.com','gmali.com','gmail.con','gmail.cmo','gmail.co','gmail.cm','gmail.vom','gmail.xom','gmail.om','gmail.comm','gmail.coom','gmail.ocm','gmaul.com','gimail.com','gmaik.com','gmailcom','gmail'],
            'yahoo.com': ['yaho.com','yahooo.com','yhaoo.com','yaoo.com','yahoo.con','yahoo.co','yahoo.cm','yahoocom','yahoo'],
            'hotmail.com': ['hotmal.com','hotmial.com','hotmil.com','hotamil.com','hotmail.con','hotmail.co','hotmailcom','hotmail'],
            'outlook.com': ['outlok.com','outllok.com','outlookcom','outlook.con','outlook.co','outlook'],
        };
        for (const [correct, typos] of Object.entries(typoMap)) {
            if (typos.includes(domain)) {
                setEmailWarning(`আপনি কি ${email.split('@')[0]}@${correct} বোঝাতে চেয়েছেন?`);
                return;
            }
        }
        if (domain && !domain.includes('.')) { setEmailWarning('আপনার ইমেইল-এ .com বা সঠিক domain নেই। সঠিক ইমেইল দিন।'); return; }
        const tld = domain.split('.').pop();
        const badTlds = ['con','cmo','co','cm','vom','xom','om','comm','coom','ocm','cpm','cim','clm','comn'];
        if (badTlds.includes(tld)) { setEmailWarning('আপনার ইমেইল-এর শেষে ভুল আছে মনে হচ্ছে। সঠিক ইমেইল (.com) দিন।'); return; }
        setEmailWarning('');
    };

    // OTP state
    const [otpStep, setOtpStep] = useState(false);
    const [otpEmail, setOtpEmail] = useState('');
    const [otpCode, setOtpCode] = useState('');
    const [otpTimer, setOtpTimer] = useState(120);

    React.useEffect(() => {
        if (!otpStep || otpTimer <= 0) return;
        const t = setInterval(() => setOtpTimer(p => p - 1), 1000);
        return () => clearInterval(t);
    }, [otpStep, otpTimer]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        const result = await login(loginEmail, loginPassword);
        if (result.success) { navigate('/dashboard'); } else {
            if (result.error === 'EMAIL_NOT_VERIFIED') { setError('Email verify করা হয়নি। Register tab থেকে আবার চেষ্টা করো।'); }
            else { setError(result.error); }
        }
        setIsLoading(false);
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        if (emailWarning) { setError('সঠিক ইমেইল দিন। আপনার ইমেইল-এ ভুল আছে মনে হচ্ছে।'); return; }
        setIsLoading(true);
        setError('');
        const result = await register(registerEmail, registerPassword, registerName, 'student', registerMobile);
        if (result.requires_otp) { setOtpStep(true); setOtpEmail(result.email); setOtpTimer(120); setError(''); }
        else if (result.success) { navigate('/dashboard'); }
        else { setError(result.error); }
        setIsLoading(false);
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        const result = await verifyOtp(otpEmail, otpCode);
        if (result.success) { navigate('/dashboard'); } else { setError(result.error); }
        setIsLoading(false);
    };

    const handleResendOtp = async () => {
        setIsLoading(true);
        setError('');
        const result = await register(otpEmail, registerPassword, registerName, 'student', registerMobile);
        if (result.requires_otp) { setOtpTimer(120); setError(''); }
        setIsLoading(false);
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-3 sm:p-6 relative overflow-hidden" data-testid="auth-page"
            style={{ background: 'linear-gradient(160deg, #F8F7FF 0%, #FAF5FF 30%, #FDF2F8 60%, #F5F3FF 100%)' }}>
            {showForgot && <ForgotPasswordModal onClose={() => setShowForgot(false)} />}

            {/* Cosmic wave background - bottom */}
            <img
                src="/images/cosmic-wave-bg.png"
                alt=""
                className="fixed bottom-0 left-0 w-full pointer-events-none select-none"
                style={{ height: '45%', objectFit: 'cover', objectPosition: 'center top', opacity: 0.18, mixBlendMode: 'screen', filter: 'brightness(1.3) saturate(1.2)' }}
            />
            {/* Cosmic wave repeat at right */}
            <img
                src="/images/cosmic-wave-bg.png"
                alt=""
                className="fixed top-0 right-0 pointer-events-none select-none"
                style={{ width: '50%', height: '70%', objectFit: 'cover', objectPosition: 'left center', opacity: 0.08, mixBlendMode: 'screen', filter: 'brightness(1.2) hue-rotate(20deg)', transform: 'rotate(90deg) scaleX(-1)' }}
            />

            {/* Ambient glow blobs */}
            <div className="fixed top-[-80px] right-[-40px] w-[300px] h-[300px] rounded-full opacity-[0.12] blur-[80px] pointer-events-none" style={{ background: 'radial-gradient(circle, #C084FC, #EC4899)' }} />
            <div className="fixed bottom-[-60px] left-[-40px] w-[250px] h-[250px] rounded-full opacity-[0.08] blur-[60px] pointer-events-none" style={{ background: 'radial-gradient(circle, #7C3AED, #A855F7)' }} />
            <div className="fixed top-[30%] left-[10%] w-[120px] h-[120px] rounded-full opacity-[0.06] blur-[40px] pointer-events-none" style={{ background: '#EC4899' }} />

            {/* Floating sparkle particles */}
            <div className="fixed inset-0 pointer-events-none overflow-hidden">
                {[...Array(6)].map((_, i) => (
                    <div key={i} className="absolute w-1 h-1 rounded-full bg-purple-400/30"
                        style={{
                            left: `${15 + i * 15}%`,
                            top: `${20 + (i % 3) * 25}%`,
                            animation: `float-sparkle ${3 + i * 0.7}s ease-in-out infinite alternate`,
                            animationDelay: `${i * 0.5}s`,
                        }}
                    />
                ))}
            </div>

            {/* Main card */}
            <div className="relative z-10 w-full max-w-[960px]">
                {/* Outer glow border */}
                <div className="absolute -inset-[1.5px] rounded-[28px] bg-gradient-to-br from-purple-400/50 via-pink-300/40 to-purple-500/50 blur-[2px]" />

                <div className="relative bg-white/85 backdrop-blur-2xl rounded-[28px] shadow-[0_20px_60px_-10px_rgba(139,92,246,0.15)] overflow-hidden flex flex-col md:flex-row" style={{ minHeight: '580px' }}>

                    {/* LEFT: Image panel */}
                    <div className="hidden md:flex md:w-[44%] relative overflow-hidden group">
                        <img
                            src={heroImg}
                            alt="Students"
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                            loading="lazy"
                            data-testid="auth-hero-image"
                        />
                        {/* Gradient overlays */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-purple-900/10" />
                    </div>

                    {/* RIGHT: Form panel */}
                    <div className="flex-1 flex flex-col px-6 py-6 sm:px-8 sm:py-7 md:px-10 md:py-8">
                        {/* Back + Logo row */}
                        <div className="flex items-center justify-between mb-4">
                            <button
                                onClick={() => navigate('/')}
                                className="flex items-center gap-1.5 text-gray-400 hover:text-purple-600 text-xs transition-colors group"
                                data-testid="auth-back-btn"
                            >
                                <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-0.5 transition-transform" />
                                <span style={tb}>হোমে ফিরে যাও</span>
                            </button>
                            <img src="/images/logo.png" alt="বাক্য শিল্পী" className="h-10 w-auto" data-testid="auth-logo" />
                        </div>

                        {/* Welcome text */}
                        <div className="mb-5 text-center">
                            <p className="text-gray-600 text-sm leading-relaxed" style={tb}>
                                <span className="font-bold text-gray-800">স্বাগতম।</span> এখানে তোমার ইংরেজির ভিত শক্ত হবে—
                                <br />Sentence Making, Grammar, IELTS preparation—
                                <br />সবকিছুর জন্য এক structured পথ।
                                <br /><span className="text-purple-600 font-medium">পরিশ্রম করলে ফল পাবেই, ইনশাআল্লাহ।</span>
                            </p>
                        </div>

                        {/* Custom Tabs */}
                        <div className="relative bg-gray-100/80 rounded-xl p-1 flex mb-5" data-testid="auth-tabs">
                            <button
                                onClick={() => { setActiveTab('login'); setError(''); }}
                                className={`flex-1 relative z-10 py-2.5 rounded-lg text-sm font-semibold transition-all duration-300 ${activeTab === 'login'
                                    ? 'text-white shadow-lg shadow-purple-500/25'
                                    : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                data-testid="login-tab"
                            >
                                {activeTab === 'login' && (
                                    <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 -z-10" />
                                )}
                                Login
                            </button>
                            <button
                                onClick={() => { setActiveTab('register'); setError(''); }}
                                className={`flex-1 relative z-10 py-2.5 rounded-lg text-sm font-semibold transition-all duration-300 ${activeTab === 'register'
                                    ? 'text-white shadow-lg shadow-purple-500/25'
                                    : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                data-testid="register-tab"
                            >
                                {activeTab === 'register' && (
                                    <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 -z-10" />
                                )}
                                Register
                            </button>
                        </div>

                        {/* Error message */}
                        {error && (
                            <div className="bg-red-50 text-red-600 px-4 py-2.5 rounded-xl mb-4 text-xs border border-red-100 animate-in fade-in" data-testid="auth-error" style={tb}>
                                {error}
                            </div>
                        )}

                        {/* Form content */}
                        <div className="flex-1">
                            {activeTab === 'login' && (
                                <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                                    <div className="space-y-1.5">
                                        <Label htmlFor="login-email" className="text-gray-600 text-xs font-medium" style={tb}>ইমেইল</Label>
                                        <div className="relative">
                                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                            <Input
                                                id="login-email"
                                                type="email"
                                                placeholder="your@email.com"
                                                value={loginEmail}
                                                onChange={e => setLoginEmail(e.target.value)}
                                                className="pl-10 bg-gray-50/80 border-gray-200/80 focus:border-purple-400 focus:ring-purple-200 rounded-xl h-11 text-sm"
                                                required
                                                data-testid="login-email"
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label htmlFor="login-password" className="text-gray-600 text-xs font-medium" style={tb}>পাসওয়ার্ড</Label>
                                        <PasswordInput
                                            id="login-password"
                                            placeholder="........"
                                            value={loginPassword}
                                            onChange={e => setLoginPassword(e.target.value)}
                                            required
                                            data-testid="login-password"
                                        />
                                    </div>
                                    <div className="flex justify-end">
                                        <button
                                            type="button"
                                            onClick={() => setShowForgot(true)}
                                            className="text-[11px] text-purple-500 hover:text-purple-700 transition-colors font-medium"
                                            style={tb}
                                            data-testid="forgot-password-link"
                                        >
                                            পাসওয়ার্ড ভুলে গেছেন?
                                        </button>
                                    </div>
                                    <Button
                                        type="submit"
                                        className="w-full bg-gradient-to-r from-purple-600 via-fuchsia-600 to-pink-600 text-white hover:opacity-90 font-bold rounded-full py-3 h-12 shadow-lg shadow-purple-500/25 hover:shadow-xl hover:shadow-purple-500/30 hover:scale-[1.02] transition-all text-sm"
                                        disabled={isLoading}
                                        data-testid="login-submit"
                                    >
                                        {isLoading ? 'Logging in...' : 'Login'}
                                    </Button>
                                </form>
                            )}

                            {activeTab === 'register' && !otpStep && (
                                <form onSubmit={handleRegister} className="space-y-3.5" data-testid="register-form">
                                    <div className="space-y-1.5">
                                        <Label htmlFor="register-name" className="text-gray-600 text-xs font-medium" style={tb}>পুরো নাম</Label>
                                        <div className="relative">
                                            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                            <Input
                                                id="register-name"
                                                type="text"
                                                placeholder="আপনার নাম"
                                                value={registerName}
                                                onChange={e => setRegisterName(e.target.value)}
                                                className="pl-10 bg-gray-50/80 border-gray-200/80 focus:border-purple-400 focus:ring-purple-200 rounded-xl h-11 text-sm"
                                                required
                                                data-testid="register-name"
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label htmlFor="register-email" className="text-gray-600 text-xs font-medium" style={tb}>ইমেইল</Label>
                                        <div className="relative">
                                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                            <Input
                                                id="register-email"
                                                type="email"
                                                placeholder="your@gmail.com"
                                                value={registerEmail}
                                                onChange={e => { setRegisterEmail(e.target.value); checkEmailTypo(e.target.value); }}
                                                onBlur={() => checkEmailTypo(registerEmail)}
                                                className={`pl-10 bg-gray-50/80 border-gray-200/80 focus:border-purple-400 focus:ring-purple-200 rounded-xl h-11 text-sm ${emailWarning ? 'border-amber-400 ring-1 ring-amber-200' : ''}`}
                                                required
                                                data-testid="register-email"
                                            />
                                        </div>
                                        {emailWarning && (
                                            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-1.5" data-testid="email-typo-warning">
                                                <Mail className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
                                                <p className="text-[11px] text-amber-700 font-medium" style={tb}>{emailWarning}</p>
                                            </div>
                                        )}
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label htmlFor="register-mobile" className="text-gray-600 text-xs font-medium" style={tb}>মোবাইল নম্বর</Label>
                                        <div className="relative">
                                            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                            <Input
                                                id="register-mobile"
                                                type="tel"
                                                placeholder="01XXXXXXXXX"
                                                value={registerMobile}
                                                onChange={e => setRegisterMobile(e.target.value)}
                                                className="pl-10 bg-gray-50/80 border-gray-200/80 focus:border-purple-400 focus:ring-purple-200 rounded-xl h-11 text-sm"
                                                required
                                                data-testid="register-mobile"
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label htmlFor="register-password" className="text-gray-600 text-xs font-medium" style={tb}>পাসওয়ার্ড</Label>
                                        <PasswordInput
                                            id="register-password"
                                            placeholder="........"
                                            value={registerPassword}
                                            onChange={e => setRegisterPassword(e.target.value)}
                                            required
                                            minLength={6}
                                            data-testid="register-password"
                                        />
                                    </div>
                                    <Button
                                        type="submit"
                                        className="w-full bg-gradient-to-r from-purple-600 via-fuchsia-600 to-pink-600 text-white hover:opacity-90 font-bold rounded-full py-3 h-12 shadow-lg shadow-purple-500/25 hover:shadow-xl hover:shadow-purple-500/30 hover:scale-[1.02] transition-all text-sm"
                                        disabled={isLoading}
                                        data-testid="register-submit"
                                    >
                                        {isLoading ? 'Sending OTP...' : 'Create Account'}
                                    </Button>
                                </form>
                            )}

                            {activeTab === 'register' && otpStep && (
                                <form onSubmit={handleVerifyOtp} className="space-y-4" data-testid="otp-form">
                                    <div className="text-center mb-2">
                                        <div className="w-14 h-14 mx-auto rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mb-3 shadow-lg shadow-purple-500/25">
                                            <Mail className="w-7 h-7 text-white" />
                                        </div>
                                        <p className="text-xs text-gray-600" style={tb}><strong>{otpEmail}</strong>-এ ৬ সংখ্যার OTP পাঠানো হয়েছে</p>
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label className="text-gray-600 text-xs font-medium" style={tb}>OTP Code</Label>
                                        <Input
                                            type="text"
                                            maxLength={6}
                                            placeholder="৬ সংখ্যার কোড"
                                            value={otpCode}
                                            onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                            className="text-center text-2xl font-bold tracking-[0.5em] bg-gray-50/80 border-gray-200 focus:border-purple-400 rounded-xl h-14"
                                            required
                                            data-testid="otp-input"
                                        />
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-[11px] text-gray-400" style={tb}>
                                            {otpTimer > 0 ? `${Math.floor(otpTimer / 60)}:${(otpTimer % 60).toString().padStart(2, '0')} বাকি` : 'OTP expired'}
                                        </span>
                                        {otpTimer <= 0 && (
                                            <button type="button" onClick={handleResendOtp}
                                                className="text-[11px] text-purple-600 font-medium hover:text-purple-800" data-testid="resend-otp-btn">
                                                Resend OTP
                                            </button>
                                        )}
                                    </div>
                                    <Button
                                        type="submit"
                                        className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:opacity-90 font-bold rounded-full py-3 h-12 shadow-lg transition-all text-sm"
                                        disabled={isLoading || otpCode.length !== 6}
                                        data-testid="verify-otp-submit"
                                    >
                                        {isLoading ? 'Verifying...' : 'Verify & Login'}
                                    </Button>
                                    <button
                                        type="button"
                                        onClick={() => { setOtpStep(false); setOtpCode(''); setError(''); }}
                                        className="w-full text-[11px] text-gray-400 hover:text-gray-600 py-1" style={tb}
                                    >
                                        <ArrowLeft className="w-3 h-3 inline mr-1" /> ফিরে যাও
                                    </button>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Sparkle animation keyframes */}
            <style>{`
                @keyframes float-sparkle {
                    0% { transform: translateY(0px) scale(1); opacity: 0.3; }
                    100% { transform: translateY(-15px) scale(1.5); opacity: 0.6; }
                }
            `}</style>
        </div>
    );
};

export default Auth;
